import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { useLang, tx } from "@/context/LangContext";
import { T } from "@/data/content";
import { auth } from "@/lib/auth";
import { FadeUp, SectionLabel } from "@/components/Reveal";
import { Check, Loader2, LogIn, Mic2, Radio, Send, UserPlus, Users, LayoutDashboard, KeyRound, Eye, EyeOff, Upload, Trash2, Clapperboard, Image as ImageIcon } from "lucide-react";
import AdminDashboard from "@/components/AdminDashboard";

const API = process.env.REACT_APP_BACKEND_URL;

const STAGE_KEYS = ["brief", "shooting", "editing", "color", "delivery"];
const SIXO_EMAIL = "silouane19@gmail.com";

// carte UPLOAD réservée à 6xo — photos & vidéos pour alimenter son espace créateur
function CreatorUploadCard({ lang }) {
    const [media, setMedia] = useState([]);
    const [uploads, setUploads] = useState([]); // {name, pct, kind}
    const [error, setError] = useState("");
    const fileRef = useRef(null);

    const load = () => fetch(`${API}/api/creator/media`).then((r) => r.json()).then((d) => setMedia(d.media || [])).catch(() => {});
    useEffect(() => { load(); }, []);

    const uploadFiles = (files) => {
        setError("");
        [...files].forEach((f) => {
            const kind = f.type.startsWith("video") ? "video" : "photo";
            const entry = { name: f.name, pct: 0, kind };
            setUploads((u) => [...u, entry]);
            const fd = new FormData();
            fd.append("file", f);
            const xhr = new XMLHttpRequest();
            xhr.open("POST", `${API}/api/creator/media/upload`);
            xhr.withCredentials = true;
            xhr.upload.onprogress = (e) => {
                if (e.lengthComputable) {
                    const pct = Math.round((e.loaded / e.total) * 100);
                    setUploads((u) => u.map((x) => (x.name === f.name ? { ...x, pct } : x)));
                }
            };
            xhr.onload = () => {
                if (xhr.status === 200) {
                    setUploads((u) => u.filter((x) => x.name !== f.name));
                    load();
                } else {
                    let msg = "Erreur d'upload";
                    try { msg = JSON.parse(xhr.responseText).detail || msg; } catch { /* ignore */ }
                    setUploads((u) => u.filter((x) => x.name !== f.name));
                    setError(`${f.name} — ${msg}`);
                }
            };
            xhr.onerror = () => {
                setUploads((u) => u.filter((x) => x.name !== f.name));
                setError(`${f.name} — erreur réseau`);
            };
            xhr.send(fd);
        });
    };

    const remove = async (mediaId) => {
        try {
            const r = await fetch(`${API}/api/creator/media/${mediaId}`, { method: "DELETE", credentials: "include" });
            if (r.ok) load();
        } catch { /* ignore */ }
    };

    return (
        <div data-testid="creator-upload-card" className="border border-fuchsia-500/40 bg-fuchsia-500/[0.04] p-6 md:p-8">
            <p className="font-mono text-[8px] tracking-[0.35em] text-fuchsia-400/70">ESPACE CRÉATEUR — 6XO</p>
            <h3 className="mt-2 font-display text-xl font-extrabold uppercase tracking-tight md:text-2xl">
                {lang === "fr" ? "ALIMENTER MON ESPACE" : "FEED MY SPACE"}
                <span className="text-fuchsia-400">.</span>
            </h3>
            <p className="mt-2 font-mono text-[9px] leading-relaxed tracking-[0.15em] text-cream/50">
                {lang === "fr"
                    ? "PHOTOS & VIDÉOS — AJOUTÉES DIRECTEMENT À TA PAGE CRÉATEUR (JPG, PNG, WEBP, MP4, MOV — 400 MO MAX)"
                    : "PHOTOS & VIDEOS — ADDED STRAIGHT TO YOUR CREATOR PAGE (JPG, PNG, WEBP, MP4, MOV — 400MB MAX)"}
            </p>

            <input
                ref={fileRef}
                type="file"
                accept="image/jpeg,image/png,image/webp,video/mp4,video/quicktime,video/x-m4v,video/webm"
                multiple
                className="hidden"
                onChange={(e) => { uploadFiles(e.target.files); e.target.value = ""; }}
            />
            <button
                data-testid="creator-upload-btn"
                onClick={() => fileRef.current?.click()}
                className="mt-4 flex items-center gap-3 border border-fuchsia-500 bg-fuchsia-500/10 px-6 py-3.5 font-mono text-[10px] tracking-[0.3em] text-fuchsia-400 transition-all hover:bg-fuchsia-500 hover:text-ink"
            >
                <Upload size={14} /> {lang === "fr" ? "UPLOAD PHOTOS / VIDÉOS" : "UPLOAD PHOTOS / VIDEOS"}
            </button>

            {/* progression */}
            {uploads.length > 0 && (
                <div className="mt-4 space-y-2">
                    {uploads.map((u) => (
                        <div key={u.name} className="border border-white/10 bg-ink/60 p-3">
                            <div className="flex items-center gap-2 font-mono text-[9px] tracking-[0.12em] text-cream/70">
                                {u.kind === "video" ? <Clapperboard size={11} className="text-fuchsia-400" /> : <ImageIcon size={11} className="text-fuchsia-400" />}
                                <span className="truncate">{u.name}</span>
                                <span className="ml-auto shrink-0 text-fuchsia-400">{u.pct}%</span>
                            </div>
                            <div className="mt-2 h-1 w-full bg-white/10">
                                <div className="h-full bg-fuchsia-500 transition-all duration-300" style={{ width: `${u.pct}%` }} />
                            </div>
                        </div>
                    ))}
                </div>
            )}
            {error && <p data-testid="creator-upload-error" className="mt-3 font-mono text-[9px] tracking-[0.15em] text-blood">{error}</p>}

            {/* médias en ligne */}
            {media.length > 0 && (
                <div className="mt-6">
                    <p className="font-mono text-[8px] tracking-[0.3em] text-cream/40">
                        {lang === "fr" ? `EN LIGNE SUR TA PAGE — ${media.length}` : `LIVE ON YOUR PAGE — ${media.length}`}
                    </p>
                    <div className="mt-3 grid grid-cols-3 gap-2 md:grid-cols-5">
                        {media.map((m) => (
                            <div key={m.media_id} className="group relative aspect-square overflow-hidden border border-white/10 bg-ink">
                                {m.kind === "video" ? (
                                    m.poster ? (
                                        <img src={m.poster} alt={m.filename} className="h-full w-full object-cover" loading="lazy" />
                                    ) : (
                                        <video src={m.url} muted preload="metadata" className="h-full w-full object-cover" />
                                    )
                                ) : (
                                    <img src={m.url} alt={m.filename} className="h-full w-full object-cover" loading="lazy" />
                                )}
                                <button
                                    data-testid={`creator-media-del-${m.media_id}`}
                                    onClick={() => remove(m.media_id)}
                                    aria-label="Supprimer"
                                    className="absolute right-1 top-1 bg-ink/80 p-1.5 text-blood opacity-100 transition-opacity md:opacity-0 md:group-hover:opacity-100"
                                >
                                    <Trash2 size={12} />
                                </button>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
}

// champ mot de passe avec œil « voir / masquer » — comme sur tous les sites
function PwInput({ testid, value, onChange, onKeyDown, placeholder, className }) {
    const [show, setShow] = useState(false);
    return (
        <div className="relative">
            <input
                data-testid={testid}
                type={show ? "text" : "password"}
                value={value}
                onChange={onChange}
                onKeyDown={onKeyDown}
                placeholder={placeholder}
                className={`${className} pr-9`}
            />
            <button
                type="button"
                data-testid={`${testid}-toggle`}
                onClick={() => setShow((s) => !s)}
                aria-label={show ? "Masquer le mot de passe" : "Voir le mot de passe"}
                className="absolute right-1.5 top-1/2 -translate-y-1/2 p-1 text-cream/40 transition-colors hover:text-cream"
            >
                {show ? <EyeOff size={14} /> : <Eye size={14} />}
            </button>
        </div>
    );
}

// changer son code personnel (comptes email/mot de passe — ex. 6xo)
function ChangePasswordCard({ lang }) {
    const [cur, setCur] = useState("");
    const [next, setNext] = useState("");
    const [confirm, setConfirm] = useState("");
    const [busy, setBusy] = useState(false);
    const [msg, setMsg] = useState(null); // {ok, text}
    const inputCls = "w-full border border-white/15 bg-white/5 px-3 py-2.5 font-mono text-[11px] tracking-[0.15em] text-cream outline-none focus:border-violet";

    const submit = async () => {
        setMsg(null);
        if (!cur || !next) return setMsg({ ok: false, text: lang === "fr" ? "Remplis les 3 champs" : "Fill all fields" });
        if (next !== confirm) return setMsg({ ok: false, text: lang === "fr" ? "Les deux nouveaux codes ne correspondent pas" : "New codes don't match" });
        if (next.length < 6) return setMsg({ ok: false, text: lang === "fr" ? "6 caractères minimum" : "6 characters minimum" });
        setBusy(true);
        try {
            const r = await fetch(`${API}/api/auth/change-password`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                credentials: "include",
                body: JSON.stringify({ current_password: cur, new_password: next }),
            });
            if (!r.ok) {
                const d = await r.json().catch(() => ({}));
                throw new Error(d.detail || "Erreur");
            }
            setMsg({ ok: true, text: lang === "fr" ? "Code changé — utilise-le à ta prochaine connexion" : "Code changed — use it next login" });
            setCur(""); setNext(""); setConfirm("");
        } catch (e) {
            setMsg({ ok: false, text: e.message });
        } finally {
            setBusy(false);
        }
    };

    return (
        <div data-testid="change-password-card" className="mt-6 border-t border-white/10 pt-5">
            <p className="flex items-center gap-2 font-mono text-[9px] tracking-[0.3em] text-cream/60">
                <KeyRound size={12} className="text-violet" /> {lang === "fr" ? "SÉCURITÉ — CHANGER MON CODE" : "SECURITY — CHANGE MY CODE"}
            </p>
            <div className="mt-3 grid gap-2 sm:grid-cols-3">
                <PwInput testid="cp-current" value={cur} onChange={(e) => setCur(e.target.value)} placeholder={lang === "fr" ? "CODE ACTUEL" : "CURRENT CODE"} className={inputCls} />
                <PwInput testid="cp-new" value={next} onChange={(e) => setNext(e.target.value)} placeholder={lang === "fr" ? "NOUVEAU CODE" : "NEW CODE"} className={inputCls} />
                <PwInput testid="cp-confirm" value={confirm} onChange={(e) => setConfirm(e.target.value)} placeholder={lang === "fr" ? "CONFIRMER" : "CONFIRM"} className={inputCls} />
            </div>
            <button data-testid="cp-submit" onClick={submit} disabled={busy}
                className="mt-3 flex items-center gap-2 border border-violet px-4 py-2.5 font-mono text-[9px] tracking-[0.25em] text-violet transition-all hover:bg-violet hover:text-cream disabled:opacity-50">
                {busy ? <Loader2 size={12} className="animate-spin" /> : <Check size={12} />}
                {lang === "fr" ? "CHANGER MON CODE" : "CHANGE MY CODE"}
            </button>
            {msg && <p data-testid="cp-msg" className={`mt-2 font-mono text-[9px] tracking-[0.15em] ${msg.ok ? "text-green-400" : "text-red-400"}`}>{msg.text}</p>}
        </div>
    );
}

// réinitialisation via lien email (#reset?token=…) — nouveau code + confirmation
function ResetPanel({ lang }) {
    const [p1, setP1] = useState("");
    const [p2, setP2] = useState("");
    const [busy, setBusy] = useState(false);
    const [done, setDone] = useState(false);
    const [error, setError] = useState("");
    const inputCls = "w-full border-b border-white/15 bg-transparent py-2 font-mono text-[11px] tracking-[0.12em] text-cream outline-none placeholder:text-cream/30 focus:border-gold";

    const submit = async () => {
        setError("");
        if (p1 !== p2) return setError(lang === "fr" ? "Les deux codes ne correspondent pas" : "Codes don't match");
        if (p1.length < 6) return setError(lang === "fr" ? "6 caractères minimum" : "6 characters minimum");
        setBusy(true);
        try {
            const r = await fetch(`${API}/api/auth/reset-password`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ token: window.__resetToken, new_password: p1 }),
            });
            const d = await r.json().catch(() => ({}));
            if (!r.ok) throw new Error(d.detail || "Erreur");
            setDone(true);
            window.__resetToken = null;
            window.location.hash = "#portal";
        } catch (e) {
            setError(e.message);
        } finally {
            setBusy(false);
        }
    };

    return (
        <div data-testid="reset-panel" className="mx-auto mt-14 max-w-lg border border-violet/40 bg-violet/5 p-8 md:p-12">
            <p className="text-center font-mono text-[9px] tracking-[0.35em] text-cream/40">GLOOM ENT. — MON ESPACE</p>
            <h2 className="mt-4 text-center font-display text-3xl font-extrabold uppercase tracking-tight md:text-4xl">
                {lang === "fr" ? "NOUVEAU CODE" : "NEW CODE"}<span className="text-violet">.</span>
            </h2>
            {done ? (
                <div className="mt-8 text-center">
                    <p data-testid="reset-success" className="font-mono text-[10px] tracking-[0.2em] text-green-400">
                        {lang === "fr" ? "CODE CHANGÉ — CONNECTE-TOI AVEC TON NOUVEAU CODE" : "CODE CHANGED — SIGN IN WITH YOUR NEW CODE"}
                    </p>
                    <a href="#portal" data-testid="reset-goto-login" className="mt-6 inline-block bg-violet px-8 py-3.5 font-mono text-[10px] tracking-[0.3em] text-cream">
                        {lang === "fr" ? "SE CONNECTER" : "SIGN IN"}
                    </a>
                </div>
            ) : (
                <div className="mt-8 space-y-5">
                    <PwInput testid="reset-new" value={p1} onChange={(e) => setP1(e.target.value)} placeholder={lang === "fr" ? "NOUVEAU MOT DE PASSE" : "NEW PASSWORD"} className={inputCls} />
                    <PwInput testid="reset-confirm" value={p2} onChange={(e) => setP2(e.target.value)} onKeyDown={(e) => e.key === "Enter" && submit()} placeholder={lang === "fr" ? "CONFIRMER LE MOT DE PASSE" : "CONFIRM PASSWORD"} className={inputCls} />
                    {error && <p data-testid="reset-error" className="font-mono text-[9px] tracking-[0.2em] text-blood">{error}</p>}
                    <button data-testid="reset-submit" onClick={submit} disabled={busy || !p1 || !p2}
                        className="flex w-full items-center justify-center gap-3 bg-violet py-4 font-mono text-[10px] tracking-[0.3em] text-cream transition-all hover:tracking-[0.45em] disabled:opacity-40">
                        {busy ? <Loader2 size={14} className="animate-spin" /> : <KeyRound size={14} />}
                        {lang === "fr" ? "VALIDER MON NOUVEAU CODE" : "SET MY NEW CODE"}
                    </button>
                </div>
            )}
        </div>
    );
}

// in-site login/register panel — dark premium, no external white page
function ChangeCodeModal({ slug, starterCode, onDone, lang }) {
    const [c1, setC1] = useState("");
    const [c2, setC2] = useState("");
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState("");
    const [ok, setOk] = useState(false);

    const submit = async () => {
        if (c1.trim().length < 6) { setErr(lang === "fr" ? "6 CARACTÈRES MINIMUM" : "6 CHARACTERS MINIMUM"); return; }
        if (c1 !== c2) { setErr(lang === "fr" ? "LES CODES NE CORRESPONDENT PAS" : "CODES DON'T MATCH"); return; }
        setBusy(true);
        setErr("");
        try {
            const r = await fetch(`${API}/api/artists/${slug}/set-code`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                credentials: "include",
                body: JSON.stringify({ current_code: starterCode, new_code: c1.trim() }),
            });
            if (!r.ok) throw new Error();
            setOk(true);
        } catch {
            setErr(lang === "fr" ? "ERREUR — RÉESSAYEZ" : "ERROR — TRY AGAIN");
        } finally {
            setBusy(false);
        }
    };

    const inputCls = "w-full border-b border-gold/30 bg-transparent py-2.5 font-mono text-[11px] tracking-[0.15em] text-cream outline-none placeholder:text-cream/30 focus:border-gold";
    return (
        <div data-testid="change-code-modal" className="fixed inset-0 z-[96] flex items-center justify-center bg-ink/90 p-5 backdrop-blur-md">
            <div className="w-full max-w-md border border-gold/50 bg-ink p-8">
                {!ok ? (
                    <>
                        <p className="font-mono text-[9px] tracking-[0.35em] text-gold">{lang === "fr" ? "SÉCURITÉ" : "SECURITY"}</p>
                        <h3 className="mt-3 font-display text-2xl font-extrabold uppercase tracking-tight">
                            {lang === "fr" ? "Créez votre code personnel" : "Create your personal code"}
                        </h3>
                        <p className="mt-2 font-mono text-[9px] leading-relaxed tracking-[0.2em] text-cream/50">
                            {lang === "fr"
                                ? "Le code de départ est partagé. Choisissez votre code personnel — il servira à vous reconnecter à votre session."
                                : "The starter code is shared. Choose your personal code — you'll use it to sign back in."}
                        </p>
                        <div className="mt-6 space-y-4">
                            <PwInput testid="new-code-1" value={c1} onChange={(e) => setC1(e.target.value)} placeholder={lang === "fr" ? "NOUVEAU MOT DE PASSE" : "NEW PASSWORD"} className={inputCls} />
                            <PwInput testid="new-code-2" value={c2} onChange={(e) => setC2(e.target.value)} onKeyDown={(e) => e.key === "Enter" && submit()} placeholder={lang === "fr" ? "CONFIRMER LE MOT DE PASSE" : "CONFIRM PASSWORD"} className={inputCls} />
                            {err && <p data-testid="code-error" className="font-mono text-[9px] tracking-[0.2em] text-blood">{err}</p>}
                            <button
                                data-testid="code-continue"
                                onClick={submit}
                                disabled={busy}
                                className="flex w-full items-center justify-center gap-2 border border-gold bg-gold/10 py-3.5 font-mono text-[10px] tracking-[0.3em] text-gold transition-all hover:bg-gold hover:text-ink disabled:opacity-40"
                            >
                                {busy ? <Loader2 size={12} className="animate-spin" /> : null}
                                {lang === "fr" ? "CONTINUER" : "CONTINUE"}
                            </button>
                        </div>
                    </>
                ) : (
                    <div className="text-center">
                        <p data-testid="code-changed" className="font-display text-2xl font-extrabold uppercase tracking-tight text-gold">
                            {lang === "fr" ? "Code personnel changé." : "Personal code changed."}
                        </p>
                        <p className="mt-3 font-mono text-[9px] leading-relaxed tracking-[0.2em] text-cream/50">
                            {lang === "fr"
                                ? "Gardez-le précieusement — c'est votre nouvelle clé d'accès."
                                : "Keep it safe — it's your new access key."}
                        </p>
                        <button
                            data-testid="code-done"
                            onClick={onDone}
                            className="mt-6 border border-gold bg-gold px-8 py-3 font-mono text-[10px] tracking-[0.3em] text-ink transition-all hover:bg-transparent hover:text-gold"
                        >
                            {lang === "fr" ? "ENTRER DANS MA PAGE" : "ENTER MY PAGE"}
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
}

function ArtistAccessForm() {
    const { lang } = useLang();
    const [roster, setRoster] = useState([]);
    const [form, setForm] = useState({ first_name: "", last_name: "", artist_name: "", instagram: "", youtube_link: "", spotify_link: "", email: "", password: "", answer: "" });
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState("");
    const [changeCode, setChangeCode] = useState(null);

    useEffect(() => {
        fetch(`${API}/api/artists`).then((r) => r.json()).then((d) => setRoster(d.artists || [])).catch(() => {});
    }, []);

    const selected = roster.find((a) => a.artist.toLowerCase() === form.artist_name.trim().toLowerCase());
    const inputCls = "w-full border-b border-white/15 bg-transparent py-2.5 font-mono text-[11px] tracking-[0.15em] text-cream outline-none placeholder:text-cream/30 focus:border-gold";

    const submit = async () => {
        if (!form.first_name.trim() || !form.last_name.trim() || !form.artist_name.trim() || !form.instagram.trim() || !form.password) {
            setErr(lang === "fr" ? "REMPLISSEZ TOUS LES CHAMPS" : "PLEASE FILL ALL FIELDS");
            return;
        }
        setBusy(true);
        setErr("");
        try {
            const r = await fetch(`${API}/api/artists/claim`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                credentials: "include",
                body: JSON.stringify(form),
            });
            if (r.status === 401) { setErr(lang === "fr" ? "MOT DE PASSE INCORRECT" : "WRONG PASSWORD"); return; }
            if (r.status === 403) { setErr(lang === "fr" ? "MAUVAISE RÉPONSE — RÉESSAYEZ" : "WRONG ANSWER — TRY AGAIN"); return; }
            if (r.status === 404) { setErr(lang === "fr" ? "CE NOM N'EST PAS DANS LE ROSTER GLOOM" : "NOT IN THE GLOOM ROSTER"); return; }
            if (!r.ok) throw new Error();
            const d = await r.json();
            window.__artistClaim = d;
            if (d.must_set_password) {
                setChangeCode({ slug: d.slug, starter: form.password });
            } else {
                window.__goPage?.("artiste");
            }
        } catch {
            setErr(lang === "fr" ? "ERREUR — RÉESSAYEZ" : "ERROR — TRY AGAIN");
        } finally {
            setBusy(false);
        }
    };

    return (
        <div data-testid="artist-access-form" className="space-y-5">
            <p className="border border-gold/30 bg-gold/5 p-3 font-mono text-[8px] leading-relaxed tracking-[0.2em] text-gold/80">
                {lang === "fr"
                    ? "ACCÈS ARTISTE GLOOM — RÉPONDEZ AUX QUESTIONS DE VOTRE PROFIL PRÉ-CRÉÉ. LE MOT DE PASSE VOUS A ÉTÉ REMIS PAR GLOOM ENT."
                    : "GLOOM ARTIST ACCESS — ANSWER YOUR PRE-BUILT PROFILE QUESTIONS. THE PASSWORD WAS GIVEN TO YOU BY GLOOM ENT."}
            </p>
            <div className="grid grid-cols-2 gap-4">
                <input data-testid="artist-firstname" value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} placeholder={lang === "fr" ? "PRÉNOM" : "FIRST NAME"} className={inputCls} />
                <input data-testid="artist-lastname" value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} placeholder={lang === "fr" ? "NOM" : "LAST NAME"} className={inputCls} />
            </div>
            <select
                data-testid="artist-name-select"
                value={form.artist_name}
                onChange={(e) => setForm({ ...form, artist_name: e.target.value, answer: "" })}
                className="w-full border-b border-white/15 bg-ink py-2.5 font-mono text-[11px] tracking-[0.15em] text-cream outline-none focus:border-gold"
            >
                <option value="">{lang === "fr" ? "VOTRE NOM D'ARTISTE…" : "YOUR ARTIST NAME…"}</option>
                {roster.map((a) => (
                    <option key={a.slug} value={a.artist}>{a.artist}</option>
                ))}
            </select>
            <input data-testid="artist-instagram" value={form.instagram} onChange={(e) => setForm({ ...form, instagram: e.target.value })} placeholder="INSTAGRAM (@…)" className={inputCls} />
            <input data-testid="artist-youtube" value={form.youtube_link} onChange={(e) => setForm({ ...form, youtube_link: e.target.value })} placeholder={lang === "fr" ? "LIEN YOUTUBE (CHAÎNE OU CLIP)" : "YOUTUBE LINK (CHANNEL OR CLIP)"} className={inputCls} />
            <input data-testid="artist-spotify" value={form.spotify_link} onChange={(e) => setForm({ ...form, spotify_link: e.target.value })} placeholder={lang === "fr" ? "LIEN SPOTIFY (OPTIONNEL)" : "SPOTIFY LINK (OPTIONAL)"} className={inputCls} />
            <input data-testid="artist-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder={lang === "fr" ? "VOTRE EMAIL" : "YOUR EMAIL"} className={inputCls} />
            {selected?.has_question && (
                <div className="border border-gold/30 bg-gold/5 p-4">
                    <p className="font-mono text-[9px] tracking-[0.2em] text-gold">{selected.question_text}</p>
                    <div className="mt-3 flex gap-3">
                        {["portugal", "espagne"].map((a) => (
                            <button
                                key={a}
                                type="button"
                                data-testid={`artist-answer-${a}`}
                                onClick={() => setForm({ ...form, answer: a })}
                                className={`flex-1 border px-4 py-2 font-mono text-[10px] tracking-[0.25em] transition-all ${form.answer === a ? "border-gold bg-gold text-ink" : "border-white/20 text-cream/60 hover:border-gold"}`}
                            >
                                {a.toUpperCase()}
                            </button>
                        ))}
                    </div>
                </div>
            )}
            <PwInput testid="artist-password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} onKeyDown={(e) => e.key === "Enter" && submit()} placeholder={lang === "fr" ? "MOT DE PASSE ARTISTE" : "ARTIST PASSWORD"} className={inputCls} />
            {err && <p data-testid="artist-access-error" className="font-mono text-[9px] tracking-[0.2em] text-blood">{err}</p>}
            <button
                data-testid="artist-access-submit"
                onClick={submit}
                disabled={busy}
                className="flex w-full items-center justify-center gap-3 border border-gold bg-gold/10 py-4 font-mono text-[10px] tracking-[0.3em] text-gold transition-all hover:bg-gold hover:text-ink disabled:opacity-40"
            >
                {busy ? <Loader2 size={14} className="animate-spin" /> : <Mic2 size={14} />}
                {lang === "fr" ? "ACCÉDER À MA PAGE ARTISTE" : "ACCESS MY ARTIST PAGE"}
            </button>
            {changeCode && (
                <ChangeCodeModal
                    slug={changeCode.slug}
                    starterCode={changeCode.starter}
                    lang={lang}
                    onDone={() => window.__goPage?.("artiste")}
                />
            )}
        </div>
    );
}

function LoginPanel({ onCancel }) {
    const { lang } = useLang();
    const [mode, setMode] = useState("login"); // login | register | forgot
    const [accountType, setAccountType] = useState("client"); // client | artist
    const [form, setForm] = useState({ email: "", password: "", name: "", instagram: "" });
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState("");
    const [forgotSent, setForgotSent] = useState(false);

    // mot de passe oublié → email de réinitialisation (réponse identique qu'un compte existe ou non)
    const submitForgot = async () => {
        setBusy(true);
        setError("");
        try {
            await fetch(`${API}/api/auth/forgot-password`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ email: form.email, origin_url: window.location.origin }),
            });
            setForgotSent(true);
        } catch {
            setError(lang === "fr" ? "Erreur d'envoi — réessaie" : "Send error — try again");
        } finally {
            setBusy(false);
        }
    };

    const submit = async () => {
        setBusy(true);
        setError("");
        try {
            if (mode === "login") await auth.loginPassword(form.email, form.password);
            else await auth.register(form.email, form.password, form.name, form.instagram, accountType);
        } catch (e) {
            setError(e.message);
        } finally {
            setBusy(false);
        }
    };

    const inputCls =
        "w-full border-b border-white/15 bg-transparent py-3 font-mono text-[11px] tracking-[0.15em] text-cream outline-none placeholder:text-cream/30 focus:border-violet";

    return (
        <div data-testid="portal-gate" className="mx-auto mt-14 max-w-lg border border-violet/40 bg-violet/5 p-8 md:p-12">
            <div className="flex items-center justify-between">
                <p className="text-center font-mono text-[9px] tracking-[0.35em] text-cream/40 md:text-[10px]">
                    GLOOM ENT. — MON ESPACE
                </p>
                {onCancel && (
                    <button data-testid="portal-gate-cancel" onClick={onCancel} className="font-mono text-[9px] tracking-[0.25em] text-cream/50 hover:text-cream">
                        ✕
                    </button>
                )}
            </div>
            <h2 className="mt-4 text-center font-display text-3xl font-extrabold uppercase tracking-tight md:text-4xl">
                {mode === "login" ? (lang === "fr" ? "CONNEXION" : "SIGN IN") : mode === "forgot" ? (lang === "fr" ? "MOT DE PASSE OUBLIÉ" : "FORGOT PASSWORD") : (lang === "fr" ? "CRÉER UN COMPTE" : "CREATE ACCOUNT")}
                <span className="text-violet">.</span>
            </h2>

            {/* vue mot de passe oublié */}
            {mode === "forgot" ? (
                <div className="mt-8 space-y-5">
                    {forgotSent ? (
                        <div className="text-center">
                            <p data-testid="forgot-success" className="font-mono text-[10px] leading-relaxed tracking-[0.15em] text-green-400">
                                {lang === "fr"
                                    ? "SI UN COMPTE EXISTE AVEC CET EMAIL, UN LIEN DE RÉINITIALISATION VIENT DE PARTIR (VALABLE 1H)."
                                    : "IF AN ACCOUNT EXISTS FOR THIS EMAIL, A RESET LINK IS ON ITS WAY (VALID 1H)."}
                            </p>
                            <button data-testid="forgot-back" onClick={() => { setMode("login"); setForgotSent(false); }}
                                className="mt-6 font-mono text-[9px] tracking-[0.25em] text-violet hover:text-cream">
                                ← {lang === "fr" ? "RETOUR À LA CONNEXION" : "BACK TO SIGN IN"}
                            </button>
                        </div>
                    ) : (
                        <>
                            <p className="text-center font-mono text-[9px] leading-relaxed tracking-[0.15em] text-cream/50">
                                {lang === "fr" ? "ENTRE TON EMAIL — ON T'ENVOIE UN LIEN POUR CHOISIR UN NOUVEAU CODE." : "ENTER YOUR EMAIL — WE'LL SEND A LINK TO SET A NEW CODE."}
                            </p>
                            <input data-testid="forgot-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} onKeyDown={(e) => e.key === "Enter" && submitForgot()} placeholder="EMAIL" className={inputCls} />
                            {error && <p data-testid="forgot-error" className="font-mono text-[9px] tracking-[0.2em] text-blood">{error}</p>}
                            <button data-testid="forgot-submit" onClick={submitForgot} disabled={busy || !form.email}
                                className="flex w-full items-center justify-center gap-3 bg-violet py-4 font-mono text-[10px] tracking-[0.3em] text-cream transition-all hover:tracking-[0.45em] disabled:opacity-40">
                                {busy ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
                                {lang === "fr" ? "ENVOYER LE LIEN" : "SEND THE LINK"}
                            </button>
                            <button data-testid="forgot-cancel" onClick={() => { setMode("login"); setError(""); }}
                                className="w-full text-center font-mono text-[9px] tracking-[0.25em] text-cream/50 hover:text-cream">
                                ← {lang === "fr" ? "RETOUR À LA CONNEXION" : "BACK TO SIGN IN"}
                            </button>
                        </>
                    )}
                </div>
            ) : (
            <>

            {/* tabs */}
            <div className="mt-8 flex border border-white/10">
                {[["login", lang === "fr" ? "CONNEXION" : "SIGN IN"], ["register", lang === "fr" ? "INSCRIPTION" : "REGISTER"]].map(([m, label]) => (
                    <button
                        key={m}
                        data-testid={`auth-tab-${m}`}
                        onClick={() => { setMode(m); setError(""); }}
                        className={`flex-1 py-3 font-mono text-[9px] tracking-[0.3em] transition-colors ${mode === m ? "bg-violet text-cream" : "text-cream/50 hover:text-cream"}`}
                    >
                        {label}
                    </button>
                ))}
            </div>

            {mode === "register" && accountType === "artist" ? (
                <div className="mt-8">
                    {/* account type choice */}
                    <div className="mb-6 flex gap-3">
                        {[
                            ["client", lang === "fr" ? "JE SUIS CLIENT" : "I'M A CLIENT", lang === "fr" ? "Projet, réservation, suivi" : "Project, booking, tracking"],
                            ["artist", lang === "fr" ? "JE SUIS ARTISTE" : "I'M AN ARTIST", lang === "fr" ? "Ma page artiste Gloom + GLM Radio" : "My Gloom artist page + GLM Radio"],
                        ].map(([val, label, hint]) => (
                            <button
                                key={val}
                                type="button"
                                data-testid={`auth-type-${val}`}
                                onClick={() => setAccountType(val)}
                                className={`flex-1 border p-4 text-left transition-all ${
                                    accountType === val
                                        ? "border-gold bg-gold/10"
                                        : "border-white/15 hover:border-white/40"
                                }`}
                            >
                                <p className={`font-mono text-[9px] tracking-[0.25em] ${accountType === val ? "text-gold" : "text-cream/60"}`}>
                                    {label}
                                </p>
                                <p className="mt-1 font-mono text-[8px] tracking-[0.1em] text-cream/35">{hint}</p>
                            </button>
                        ))}
                    </div>
                    <ArtistAccessForm />
                </div>
            ) : (
            <>
            <div className="mt-8 space-y-5">
                {mode === "register" && (
                    <>
                        {/* account type choice */}
                        <div className="flex gap-3">
                            {[
                                ["client", lang === "fr" ? "JE SUIS CLIENT" : "I'M A CLIENT", lang === "fr" ? "Projet, réservation, suivi" : "Project, booking, tracking"],
                                ["artist", lang === "fr" ? "JE SUIS ARTISTE" : "I'M AN ARTIST", lang === "fr" ? "Livrer mes sons sur GLM Radio" : "Deliver my tracks on GLM Radio"],
                            ].map(([val, label, hint]) => (
                                <button
                                    key={val}
                                    type="button"
                                    data-testid={`auth-type-${val}`}
                                    onClick={() => setAccountType(val)}
                                    className={`flex-1 border p-4 text-left transition-all ${
                                        accountType === val
                                            ? "border-gold bg-gold/10"
                                            : "border-white/15 hover:border-white/40"
                                    }`}
                                >
                                    <p className={`font-mono text-[9px] tracking-[0.25em] ${accountType === val ? "text-gold" : "text-cream/60"}`}>
                                        {label}
                                    </p>
                                    <p className="mt-1 font-mono text-[8px] tracking-[0.1em] text-cream/35">{hint}</p>
                                </button>
                            ))}
                        </div>
                        <input data-testid="auth-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder={accountType === "artist" ? (lang === "fr" ? "NOM D'ARTISTE" : "ARTIST NAME") : (lang === "fr" ? "PRÉNOM & NOM" : "FIRST & LAST NAME")} className={inputCls} />
                        <input data-testid="auth-instagram" value={form.instagram} onChange={(e) => setForm({ ...form, instagram: e.target.value })} placeholder="INSTAGRAM (@…)" className={inputCls} />
                    </>
                )}
                <input data-testid="auth-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="EMAIL" className={inputCls} />
                <PwInput testid="auth-password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} onKeyDown={(e) => e.key === "Enter" && submit()} placeholder={lang === "fr" ? "MOT DE PASSE (6 MIN.)" : "PASSWORD (6 MIN.)"} className={inputCls} />
                {mode === "login" && (
                    <button
                        data-testid="auth-forgot-link"
                        type="button"
                        onClick={() => { setMode("forgot"); setError(""); }}
                        className="w-full text-right font-mono text-[8px] tracking-[0.25em] text-cream/40 transition-colors hover:text-violet"
                    >
                        {lang === "fr" ? "MOT DE PASSE OUBLIÉ ?" : "FORGOT PASSWORD?"}
                    </button>
                )}
            </div>

            {error && (
                <p data-testid="auth-error" className="mt-4 font-mono text-[9px] tracking-[0.2em] text-blood">
                    {error}
                </p>
            )}

            <button
                data-testid="auth-submit"
                onClick={submit}
                disabled={busy || !form.email || !form.password || (mode === "register" && !form.name)}
                className="mt-8 flex w-full items-center justify-center gap-3 bg-violet py-4 font-mono text-[10px] tracking-[0.3em] text-cream transition-all duration-500 hover:tracking-[0.45em] disabled:opacity-40"
            >
                {busy ? <Loader2 size={14} className="animate-spin" /> : mode === "login" ? <LogIn size={14} /> : <UserPlus size={14} />}
                {mode === "login" ? (lang === "fr" ? "SE CONNECTER" : "SIGN IN") : (lang === "fr" ? "CRÉER MON COMPTE" : "CREATE MY ACCOUNT")}
            </button>

            <div className="mt-6 flex items-center gap-4">
                <span className="h-px flex-1 bg-white/10" />
                <span className="font-mono text-[8px] tracking-[0.3em] text-cream/30">OU</span>
                <span className="h-px flex-1 bg-white/10" />
            </div>

            <button
                data-testid="portal-google-btn"
                onClick={() => auth.login()}
                className="mt-6 flex w-full items-center justify-center gap-3 border border-white/20 py-4 font-mono text-[9px] tracking-[0.3em] text-cream/70 transition-colors hover:border-violet hover:text-cream"
            >
                <LogIn size={12} /> {lang === "fr" ? "CONTINUER AVEC GOOGLE" : "CONTINUE WITH GOOGLE"}
            </button>
            </>
            )}
            </>
            )}
        </div>
    );
}

export default function Portal() {
    const { lang } = useLang();
    const [state, setState] = useState(auth.state);
    const [showLogin, setShowLogin] = useState(false); // forcer le formulaire même si connecté
    const [project, setProject] = useState(null);
    const [clients, setClients] = useState(null);
    const [messages, setMessages] = useState([]);
    const [input, setInput] = useState("");
    const [sending, setSending] = useState(false);
    const [summarized, setSummarized] = useState(false);
    const bottomRef = useRef(null);

    useEffect(() => auth.subscribe(setState), []);
    // si l'utilisateur clique « SE CONNECTER » alors qu'il est déjà connecté → afficher le formulaire
    useEffect(() => {
        const onForce = () => setShowLogin(true);
        window.addEventListener("portal:show-login", onForce);
        return () => window.removeEventListener("portal:show-login", onForce);
    }, []);
    useEffect(() => {
        if (state.status !== "authed") return;
        fetch(`${API}/api/my/project`, { credentials: "include" })
            .then((r) => r.json())
            .then((d) => setProject(d.project))
            .catch(() => {});
        if (state.user?.is_admin) {
            fetch(`${API}/api/admin/clients`, { credentials: "include" })
                .then((r) => r.json())
                .then((d) => setClients(d.clients || []))
                .catch(() => {});
        }
    }, [state.status, state.user]);
    useEffect(() => {
        bottomRef.current?.scrollIntoView({ behavior: "smooth" });
    }, [messages]);

    const send = async () => {
        if (!input.trim() || sending) return;
        const text = input.trim();
        setInput("");
        setSending(true);
        setMessages((m) => [...m, { role: "user", content: text }]);
        try {
            const r = await fetch(`${API}/api/portal/chat`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                credentials: "include",
                body: JSON.stringify({ message: text, session_id: state.user.user_id }),
            });
            const reader = r.body.getReader();
            const decoder = new TextDecoder();
            let acc = "";
            setMessages((m) => [...m, { role: "assistant", content: "" }]);
            while (true) {
                const { done, value } = await reader.read();
                if (done) break;
                const chunk = decoder.decode(value);
                for (const line of chunk.split("\n")) {
                    if (line.startsWith("data: ")) {
                        try {
                            const d = JSON.parse(line.slice(6));
                            if (d.delta) {
                                acc += d.delta;
                                const display = acc
                                    .replace(/\[\[NAV:[a-z]*\]\]/gi, "")
                                    .replace(/\[\[?\s*N?A?V?:?[a-z]*$/i, "");
                                setMessages((m) => [...m.slice(0, -1), { role: "assistant", content: display }]);
                            }
                        } catch {}
                    }
                }
            }
            const nav = acc.match(/\[\[NAV:(home|videos|photo|contact|radio|portal)\]\]/i);
            if (nav) {
                setTimeout(() => window.__goPage?.(nav[1].toLowerCase()), 350);
            }
        } finally {
            setSending(false);
        }
    };

    const summarize = async () => {
        const r = await fetch(`${API}/api/portal/chat/summarize`, { method: "POST", credentials: "include" });
        if (r.ok) setSummarized(true);
    };

    const stages = T.admin.projectStages;

    return (
        <section data-testid="portal-page" className="relative min-h-screen bg-ink px-5 pb-24 pt-32 text-cream md:px-10">
            <div className="relative mx-auto max-w-[1200px]">
                <SectionLabel en={tx(lang, T.portal.label)} fr={tx(lang, T.portal.label)} />
                <FadeUp delay={0.1}>
                    <h1 className="mt-8 font-display text-[clamp(2.6rem,8vw,7rem)] font-extrabold uppercase leading-none tracking-[-0.02em]">
                        {tx(lang, T.portal.title)}
                        <span className="text-violet">.</span>
                    </h1>
                </FadeUp>

                {state.status === "checking" && (
                    <div data-testid="portal-loading" className="mt-14 flex justify-center border border-white/10 p-12">
                        <Loader2 size={20} className="animate-spin text-violet" />
                    </div>
                )}

                {/* réinitialisation via lien email (#reset?token=…) — prioritaire sur le login */}
                {window.__resetToken && (
                    <FadeUp delay={0.1}>
                        <ResetPanel lang={lang} />
                    </FadeUp>
                )}

                {!window.__resetToken && (state.status === "guest" || (state.status === "authed" && showLogin)) && (
                    <FadeUp delay={0.15}>
                        {state.status === "authed" && (
                            <p className="mt-6 text-center font-mono text-[9px] tracking-[0.3em] text-cream/50">
                                {lang === "fr" ? `CONNECTÉ EN TANT QUE ${state.user?.email} — CONNECTEZ-VOUS À UN AUTRE COMPTE` : `SIGNED IN AS ${state.user?.email} — SIGN IN TO ANOTHER ACCOUNT`}
                                {" "}
                                <button data-testid="portal-cancel-login" onClick={() => setShowLogin(false)} className="ml-2 border-b border-violet text-violet">
                                    {lang === "fr" ? "RETOUR À MON PROJET" : "BACK TO MY PROJECT"}
                                </button>
                            </p>
                        )}
                        <LoginPanel />
                    </FadeUp>
                )}

                {state.status === "authed" && !showLogin && (
                    <>
                        {/* UPLOAD 6XO — réservé à son compte : photos & vidéos pour sa page créateur */}
                        {state.user?.email === SIXO_EMAIL && (
                            <FadeUp delay={0.02} className="mt-8">
                                <CreatorUploadCard lang={lang} />
                            </FadeUp>
                        )}

                        {/* SÉCURITÉ — changer son code (6xo & comptes email) */}
                        <FadeUp delay={0.02} className="mt-8">
                            <div className="border border-white/10 bg-white/[0.02] p-6 md:p-8">
                                <ChangePasswordCard lang={lang} />
                            </div>
                        </FadeUp>

                        {/* DASHBOARD ADMIN — vous (Gloom) et 6xo : toutes les données du site */}
                        {state.user?.is_admin && (
                            <FadeUp delay={0.05} className="mt-10">
                                <div data-testid="portal-admin-section" className="border border-gold/40 bg-gold/[0.04] p-7 md:p-9">
                                    <p className="flex items-center gap-3 font-mono text-[11px] tracking-[0.35em] text-gold">
                                        <LayoutDashboard size={15} /> {lang === "fr" ? "TABLEAU DE BORD ADMIN" : "ADMIN DASHBOARD"}
                                        <span className="ml-2 border border-gold/50 px-2 py-0.5 text-[8px] tracking-[0.25em]">{state.user.email}</span>
                                    </p>
                                    <p className="mt-2 font-mono text-[9px] tracking-[0.25em] text-cream/45">
                                        {lang === "fr" ? "TOUTES LES DONNÉES DU SITE — RENDEMENT, CLIENTS, PROJETS" : "ALL SITE DATA — PERFORMANCE, CLIENTS, PROJECTS"}
                                    </p>
                                    <div className="mt-6">
                                        <AdminDashboard />
                                    </div>
                                </div>
                            </FadeUp>
                        )}

                        {/* GLM Radio pastille */}
                        <FadeUp delay={0.05} className="mt-8">
                            <button
                                data-testid="portal-radio-pill"
                                onClick={() => window.__goPage?.("radio")}
                                className="inline-flex items-center gap-3 rounded-full border border-violet/50 bg-violet/10 px-5 py-2.5 font-mono text-[9px] tracking-[0.3em] text-violet transition-all hover:bg-violet hover:text-cream"
                            >
                                <Radio size={12} className="animate-pulse" /> GLM RADIO
                            </button>
                        </FadeUp>

                        {/* artist space — track delivery via GLM Radio */}
                        {state.user?.account_type === "artist" && (
                            <FadeUp delay={0.08} className="mt-8">
                                <div data-testid="portal-artist" className="border border-violet/40 bg-violet/5 p-7 md:p-9">
                                    <p className="font-mono text-[10px] tracking-[0.3em] text-violet">
                                        {lang === "fr" ? "ESPACE ARTISTE" : "ARTIST SPACE"}
                                    </p>
                                    <p className="mt-3 max-w-lg font-mono text-[9px] leading-relaxed tracking-[0.2em] text-cream/55">
                                        {lang === "fr"
                                            ? "Livrez vos sons pour GLM Radio — chaque morceau est écouté et validé par Gloom Ent. avant diffusion."
                                            : "Deliver your tracks for GLM Radio — every track is listened to and validated by Gloom Ent. before broadcast."}
                                    </p>
                                    <button
                                        data-testid="portal-artist-submit"
                                        onClick={() => window.__goPage?.("radio")}
                                        className="mt-5 inline-flex items-center gap-3 border border-violet/60 px-6 py-3 font-mono text-[9px] tracking-[0.3em] text-violet transition-all hover:bg-violet hover:text-cream"
                                    >
                                        <Radio size={12} /> {lang === "fr" ? "PROPOSER UN SON" : "SUBMIT A TRACK"}
                                    </button>
                                </div>
                            </FadeUp>
                        )}

                        {/* progress */}
                        {project && (
                            <FadeUp delay={0.1} className="mt-12">
                                <div className="border border-white/10 p-7 md:p-9" data-testid="portal-project">
                                    <div className="flex items-baseline justify-between gap-4">
                                        <div>
                                            <p className="font-mono text-[9px] tracking-[0.35em] text-cream/40">
                                                {lang === "fr" ? "PROJET EN COURS" : "CURRENT PROJECT"}
                                            </p>
                                            <p className="mt-2 font-display text-2xl font-extrabold uppercase tracking-tight md:text-3xl">
                                                {project.name}
                                            </p>
                                        </div>
                                        <p className="font-mono text-[9px] tracking-[0.3em] text-gold">{project.project_type}</p>
                                    </div>
                                    <div className="mt-8">
                                        <div className="h-1.5 w-full overflow-hidden bg-white/10">
                                            <motion.div
                                                initial={{ width: 0 }}
                                                animate={{ width: `${((project.stage + 1) / stages.length) * 100}%` }}
                                                transition={{ duration: 1, ease: [0.16, 1, 0.3, 1] }}
                                                className="h-full bg-gradient-to-r from-violet via-blood to-gold"
                                            />
                                        </div>
                                        <div className="mt-4 flex justify-between">
                                            {stages.map((s, i) => (
                                                <div key={s.en} className="flex flex-col items-center gap-2">
                                                    <span
                                                        className={`flex h-6 w-6 items-center justify-center rounded-full border font-mono text-[8px] ${
                                                            i <= project.stage
                                                                ? "border-gold bg-gold text-ink"
                                                                : "border-white/20 text-cream/35"
                                                        }`}
                                                    >
                                                        {i <= project.stage ? <Check size={10} /> : i + 1}
                                                    </span>
                                                    <span className={`font-mono text-[7px] tracking-[0.2em] md:text-[8px] ${i <= project.stage ? "text-cream/70" : "text-cream/30"}`}>
                                                        {s[lang]}
                                                    </span>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            </FadeUp>
                        )}

                        {/* chat */}
                        <FadeUp delay={0.15} className="mt-12">
                            <div className="border border-white/10 p-7 md:p-9">
                                <p className="flex items-center gap-3 font-mono text-[10px] tracking-[0.3em] text-violet">
                                    <Send size={13} /> {tx(lang, T.portal.chatTitle)}
                                </p>
                                <p className="mt-2 font-mono text-[9px] tracking-[0.25em] text-cream/40">
                                    {tx(lang, T.portal.chatNote)}
                                </p>
                                <div data-testid="portal-chat" className="mt-6 h-64 space-y-3 overflow-y-auto border border-white/5 p-4">
                                    {messages.length === 0 && (
                                        <p className="font-mono text-[9px] tracking-[0.2em] text-cream/30">
                                            {lang === "fr" ? "Décrivez votre idée, votre ambiance, vos références…" : "Describe your idea, your vibe, your references…"}
                                        </p>
                                    )}
                                    {messages.map((m, i) => (
                                        <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                                            <div className={`max-w-[80%] px-4 py-2 font-mono text-[10px] leading-relaxed tracking-[0.1em] ${
                                                m.role === "user" ? "bg-violet/20 text-cream" : "bg-white/5 text-cream/80"
                                            }`}>
                                                {m.content}
                                            </div>
                                        </div>
                                    ))}
                                    <div ref={bottomRef} />
                                </div>
                                <div className="mt-4 flex gap-3">
                                    <input
                                        data-testid="portal-input"
                                        value={input}
                                        onChange={(e) => setInput(e.target.value)}
                                        onKeyDown={(e) => e.key === "Enter" && send()}
                                        placeholder={lang === "fr" ? "Votre message…" : "Your message…"}
                                        className="w-full border-b border-white/15 bg-transparent py-2 font-mono text-[11px] tracking-[0.15em] text-cream outline-none placeholder:text-cream/30 focus:border-violet"
                                    />
                                    <button
                                        data-testid="portal-send"
                                        onClick={send}
                                        disabled={sending || !input.trim()}
                                        className="flex items-center gap-2 border border-violet/60 px-5 py-2 font-mono text-[9px] tracking-[0.25em] text-violet transition-all hover:bg-violet hover:text-cream disabled:opacity-30"
                                    >
                                        {sending ? <Loader2 size={12} className="animate-spin" /> : <Send size={12} />}
                                    </button>
                                </div>
                                <button
                                    data-testid="portal-summarize"
                                    onClick={summarize}
                                    disabled={messages.length === 0 || summarized}
                                    className="mt-5 font-mono text-[8px] tracking-[0.25em] text-cream/40 transition-colors hover:text-gold disabled:opacity-30"
                                >
                                    {summarized ? (lang === "fr" ? "RÉSUMÉ ENVOYÉ À GLOOM" : "SUMMARY SENT TO GLOOM") : (lang === "fr" ? "ENVOYER LE COMPTE RENDU À GLOOM" : "SEND SUMMARY TO GLOOM")}
                                </button>
                            </div>
                        </FadeUp>

                        {/* clients — admin only */}
                        {state.user?.is_admin && clients && (
                            <FadeUp delay={0.2} className="mt-12">
                                <div className="border border-gold/30 p-7 md:p-9" data-testid="portal-clients">
                                    <p className="flex items-center gap-3 font-mono text-[10px] tracking-[0.3em] text-gold">
                                        <Users size={13} /> CLIENTS — {clients.length}
                                    </p>
                                    <div className="mt-6 overflow-x-auto">
                                        <table className="w-full text-left font-mono text-[10px] tracking-[0.1em]">
                                            <thead>
                                                <tr className="border-b border-white/10 text-cream/40">
                                                    <th className="px-3 py-2 font-normal uppercase">{lang === "fr" ? "NOM PRÉNOM" : "NAME"}</th>
                                                    <th className="px-3 py-2 font-normal uppercase">INSTAGRAM</th>
                                                    <th className="px-3 py-2 font-normal uppercase">EMAIL</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {clients.map((c) => (
                                                    <tr key={c.email} data-testid={`portal-client-${c.email}`} className="border-b border-white/5 text-cream/80">
                                                        <td className="px-3 py-2.5">{c.name || "—"}</td>
                                                        <td className="px-3 py-2.5 text-violet">{c.instagram || "—"}</td>
                                                        <td className="px-3 py-2.5">{c.email}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            </FadeUp>
                        )}
                    </>
                )}
            </div>
        </section>
    );
}
